import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import {
  CampaignRecipient,
  CampaignRecipientSendStatus,
  CAMPAIGN_RECIPIENT_SEND_STATUSES,
  parseSendOccurrence,
} from "../leads/marketingCampaign";

const parseStatus = (value: unknown): CampaignRecipientSendStatus =>
  typeof value === "string" &&
  (CAMPAIGN_RECIPIENT_SEND_STATUSES as string[]).includes(value)
    ? (value as CampaignRecipientSendStatus)
    : "queued";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const serializeDateNullable = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const campaignRecipientConverter: FirestoreDataConverter<CampaignRecipient> = {
  toFirestore(recipient: CampaignRecipient) {
    const { id: _id, ...rest } = recipient;
    const createdAt =
      rest.createdAt instanceof Date && !Number.isNaN(rest.createdAt.getTime())
        ? rest.createdAt
        : new Date();
    const updatedAt =
      rest.updatedAt instanceof Date && !Number.isNaN(rest.updatedAt.getTime())
        ? rest.updatedAt
        : createdAt;
    const error = typeof rest.error === "string" ? rest.error.trim() : "";
    const mailDocId = typeof rest.mailDocId === "string" ? rest.mailDocId.trim() : "";
    return {
      campaignId: rest.campaignId ?? "",
      contactId: rest.contactId ?? "",
      email: rest.email?.trim() ?? "",
      status: parseStatus(rest.status),
      ...(error ? { error } : {}),
      ...(mailDocId ? { mailDocId } : {}),
      unsubscribeToken: rest.unsubscribeToken ?? "",
      occurrence: parseSendOccurrence(rest.occurrence),
      sentAt: serializeDateNullable(rest.sentAt ?? null),
      unsubscribedAt: serializeDateNullable(rest.unsubscribedAt ?? null),
      createdAt: serializeDate(createdAt),
      updatedAt: serializeDate(updatedAt),
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): CampaignRecipient {
    const data = snapshot.data(options) ?? {};
    const createdAt = convertTimestamp(data.createdAt) || new Date();
    const error = typeof data.error === "string" ? data.error.trim() : "";
    const mailDocId = typeof data.mailDocId === "string" ? data.mailDocId.trim() : "";
    return {
      id: snapshot.id,
      campaignId: typeof data.campaignId === "string" ? data.campaignId : "",
      contactId: typeof data.contactId === "string" ? data.contactId : "",
      email: typeof data.email === "string" ? data.email : "",
      status: parseStatus(data.status),
      ...(error ? { error } : {}),
      ...(mailDocId ? { mailDocId } : {}),
      unsubscribeToken:
        typeof data.unsubscribeToken === "string" ? data.unsubscribeToken : "",
      occurrence: parseSendOccurrence(data.occurrence),
      sentAt: convertTimestamp(data.sentAt),
      unsubscribedAt: convertTimestamp(data.unsubscribedAt),
      createdAt,
      updatedAt: convertTimestamp(data.updatedAt) || createdAt,
    };
  },
};
